import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiSearch, FiFilter, FiDownload, FiUpload, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';

interface Volume {
  id: number;
  crop_id: number;
  market_name: string;
  date: string;
  volume_bags: number;
  volume_quintal: string;
  created_at: string;
  crop_name: string;
  crop_name_hindi: string;
}

interface Crop {
  id: number;
  name: string;
  name_hindi: string;
}

const VolumeSection: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMarket, setFilterMarket] = useState('all');
  const [filterCrop, setFilterCrop] = useState('all');
  const [volumes, setVolumes] = useState<Volume[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Add Volume Entry state
  const [crops, setCrops] = useState<Crop[]>([]);
  const [markets, setMarkets] = useState<string[]>([]);
  const [addForm, setAddForm] = useState({
    crop_id: '',
    market_name: '',
    date: new Date().toISOString().slice(0, 10), // yyyy-mm-dd
    volume_bags: '',
    volume_quintal: '',
  });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');

  // Fetch crops for dropdown
  useEffect(() => {
    const cacheBuster = new Date().getTime();
    fetch(`https://mandilive.in/api/crops?_cb=${cacheBuster}`)
      .then(res => res.json())
      .then(data => setCrops(Array.isArray(data.data) ? data.data : []));
  }, []);

  // Update markets from volumes
  useEffect(() => {
    setMarkets(Array.from(new Set(volumes.map(v => v.market_name))));
  }, [volumes]);

  // Prefill if entry exists for crop/market/date
  useEffect(() => {
    if (!addForm.crop_id || !addForm.market_name || !addForm.date) return;
    const existing = volumes.find(v => String(v.crop_id) === String(addForm.crop_id) && v.market_name === addForm.market_name && (v.date === addForm.date || v.date === toHindiDate(addForm.date)));
    if (existing) {
      setAddForm(f => ({
        ...f,
        volume_bags: existing.volume_bags.toString(),
        volume_quintal: existing.volume_quintal,
      }));
    } else {
      setAddForm(f => ({ ...f, volume_bags: '', volume_quintal: '' }));
    }
    // eslint-disable-next-line
  }, [addForm.crop_id, addForm.market_name, addForm.date, volumes]);

  function toHindiDate(dateStr: string) {
    const d = new Date(dateStr);
    return d.toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  const handleAddFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAddForm(f => ({ ...f, [name]: value }));
  };

  // Add Volume Entry handler
  const handleAddVolume = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError('');
    setAddSuccess('');
    setAddLoading(true);
    try {
      // Check if entry exists
      const existing = volumes.find(v => String(v.crop_id) === String(addForm.crop_id) && v.market_name === addForm.market_name && (v.date === addForm.date || v.date === toHindiDate(addForm.date)));
      // Convert date to Hindi format for API
      const hindiDate = toHindiDate(addForm.date);
      const isoDate = addForm.date;
      const payload = {
        crop_id: Number(addForm.crop_id),
        market_name: addForm.market_name,
        date: hindiDate,
        date_iso: isoDate,
        volume_bags: Number(addForm.volume_bags),
        volume_quintal: addForm.volume_quintal,
      };
      const url = 'https://mandilive.in/api/endpoints/volume_data.php';
      const method = existing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setAddSuccess(existing ? 'Volume entry updated.' : 'Volume entry added.');
        setAddError('');
        // Refresh volumes
        const r = await fetch(url);
        const d = await r.json();
        setVolumes(Array.isArray(d.data) ? d.data : []);
      } else {
        let err;
        try {
          err = await res.json();
        } catch (e) {
          err = { message: res.statusText };
        }
        console.error('API error:', res.status, err);
        if (res.status === 409 && method === 'POST') {
          setAddError('A volume entry already exists for this crop, market, and date.');
        } else if (res.status === 404 && method === 'PUT') {
          setAddError('No matching record found for update. Please check crop, market, and date.');
        } else {
          setAddError(err.message || 'Failed to save volume entry');
        }
      }
    } catch (err: any) {
      setAddError(err?.message || 'Failed to save volume entry');
    }
    setAddLoading(false);
  };

// Fetch volume data from API on mount
  React.useEffect(() => {
    const fetchVolumes = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch('https://mandilive.in/api/endpoints/volume_data.php');
        const data = await res.json();
        setVolumes(Array.isArray(data.data) ? data.data : []);
      } catch (err: any) {
        setError('Failed to fetch volume data: ' + (err?.message || err));
        setVolumes([]);
      }
      setLoading(false);
    };
    fetchVolumes();
  }, []);

  // Get today's date in Hindi format (to match API)
  const today = new Date();
  const todayHindi = today.toLocaleDateString('hi-IN', { day: 'numeric', month: 'long', year: 'numeric' });

  // Filter volumes based on search and filters
  const filteredVolumes = volumes.filter(volume => {
    const matchesSearch = !searchTerm || 
      volume.crop_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      volume.crop_name_hindi?.includes(searchTerm) ||
      volume.market_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesMarket = filterMarket === 'all' || volume.market_name === filterMarket;
    const matchesCrop = filterCrop === 'all' || String(volume.crop_id) === filterCrop;
    
    return matchesSearch && matchesMarket && matchesCrop;
  });

  // Group by crop and market for today/last available view
  const groupedVolumes = filteredVolumes.reduce((acc, volume) => {
    const key = `${volume.crop_id}-${volume.market_name}`;
    if (!acc[key]) {
      acc[key] = {
        crop_id: volume.crop_id,
        crop_name: volume.crop_name,
        crop_name_hindi: volume.crop_name_hindi,
        market_name: volume.market_name,
        todayVolume: null as Volume | null,
        lastVolume: null as Volume | null,
        allVolumes: [] as Volume[]
      };
    }
    
    acc[key].allVolumes.push(volume);
    
    if (volume.date === todayHindi) {
      acc[key].todayVolume = volume;
    }
    
    return acc;
  }, {} as Record<string, any>);

  // Set last available volume for entries without today's data
  Object.values(groupedVolumes).forEach((group: any) => {
    if (!group.todayVolume && group.allVolumes.length > 0) {
      group.lastVolume = group.allVolumes.sort((a: Volume, b: Volume) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
    }
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Volume Management</h1>
       
      </div>

      {/* Add Volume Entry Form */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <FiPlus className="mr-2" />
          Add/Update Volume Entry
        </h2>
        <form onSubmit={handleAddVolume} className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Crop</label>
            <select
              name="crop_id"
              value={addForm.crop_id}
              onChange={handleAddFormChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Select Crop</option>
              {crops.map(crop => (
                <option key={crop.id} value={crop.id}>
                  {crop.name} ({crop.name_hindi})
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Market</label>
            <input
              type="text"
              name="market_name"
              value={addForm.market_name}
              onChange={handleAddFormChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Market Name"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              name="date"
              value={addForm.date}
              onChange={handleAddFormChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Volume (Bags)</label>
            <input
              type="number"
              name="volume_bags"
              value={addForm.volume_bags}
              onChange={handleAddFormChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0"
              min="0"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Volume (Quintal)</label>
            <input
              type="text"
              name="volume_quintal"
              value={addForm.volume_quintal}
              onChange={handleAddFormChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00"
              required
            />
          </div>
          
          <div className="flex items-end">
            <button
              type="submit"
              disabled={addLoading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {addLoading ? 'Saving...' : (
                <>
                  <FiPlus className="mr-2" />
                  Save Entry
                </>
              )}
            </button>
          </div>
        </form>
        
        {addError && (
          <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {addError}
          </div>
        )}
        
        {addSuccess && (
          <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {addSuccess}
          </div>
        )}
      </div>

    

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading volume data...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Today's/Last Available Volume Summary */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <FiTrendingUp className="mr-2 text-green-600" />
            Today's Volume Summary ({todayHindi})
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Showing today's volume data or last available data for each crop-market combination
          </p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Crop</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Crop (Hindi)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Market</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Volume (Bags)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Volume (Quintal)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Object.values(groupedVolumes).map((group: any) => {
                const volume = group.todayVolume || group.lastVolume;
                if (!volume) return null;
                
                const isToday = group.todayVolume !== null;
                
                return (
                  <tr key={`${group.crop_id}-${group.market_name}`} className={isToday ? 'bg-green-50' : 'bg-yellow-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {volume.crop_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {volume.crop_name_hindi}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {volume.market_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {volume.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {volume.volume_bags}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {volume.volume_quintal}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        isToday 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {isToday ? (
                          <>
                            <FiTrendingUp className="mr-1" />
                            Today
                          </>
                        ) : (
                          <>
                            <FiTrendingDown className="mr-1" />
                            Last Available
                          </>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {Object.keys(groupedVolumes).length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              <p>No volume data found matching your criteria.</p>
            </div>
          )}
        </div>
      </div>

      {/* All Volume Data Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">All Volume Data</h2>
          <p className="text-sm text-gray-600 mt-1">
            Complete historical volume data for all crops and markets
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Crop</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Crop (Hindi)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Market</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Volume (Bags)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Volume (Quintal)</th>
        
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredVolumes.map((volume, index) => (
                <tr key={volume.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{volume.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{volume.crop_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{volume.crop_name_hindi}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{volume.market_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{volume.date}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{volume.volume_bags}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{volume.volume_quintal}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => {
                          setAddForm({
                            crop_id: volume.crop_id.toString(),
                            market_name: volume.market_name,
                            date: new Date(volume.date).toISOString().slice(0, 10),
                            volume_bags: volume.volume_bags.toString(),
                            volume_quintal: volume.volume_quintal,
                          });
                        }}
                        className="text-blue-600 hover:text-blue-900 flex items-center"
                      >
                        
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredVolumes.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-500">
              <p>No volume data found matching your criteria.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VolumeSection;
